/**
 * Layout condiviso per le pagine legali (Termini, Privacy).
 * Editorial × solar style allineato al wizard.
 */

import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LegalDocument, LegalParagraph, LegalSection } from '../../data/legal/types';

interface LegalPageLayoutProps {
  doc: LegalDocument;
}

export default function LegalPageLayout({ doc }: LegalPageLayoutProps) {
  const { t: tCommon } = useTranslation('common');

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Top bar */}
      <Box
        component="header"
        sx={{
          padding: { xs: '18px 24px', md: '20px 56px' },
          borderBottom: '1px solid var(--dust)',
        }}
      >
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--sun)' }} />
            {tCommon('brand')}
          </Typography>
        </Link>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        {/* Hero */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--sun-deep)',
              mb: 1.5,
            }}
          >
            {doc.kind === 'termini_servizio' ? 'Termini di servizio' : 'Privacy policy'}
            {' · v'}{doc.versione}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Fraunces Variable", Georgia, serif',
              fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              color: 'var(--ink)',
              mb: 1.5,
            }}
          >
            {doc.titolo}
          </Typography>
          {doc.sottotitolo && (
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontSize: '1.15rem',
                color: 'var(--ink-soft)',
                lineHeight: 1.5,
                maxWidth: 720,
                mb: 2,
              }}
            >
              {doc.sottotitolo}
            </Typography>
          )}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 3 }}
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.72rem',
              letterSpacing: '0.08em',
              color: 'var(--ink-soft)',
              mt: 2,
            }}
          >
            <Box>Versione: {doc.versione}</Box>
            <Box>Ultima modifica: {formatDate(doc.data_ultima_modifica)}</Box>
            <Box>In vigore dal: {formatDate(doc.data_efficacia)}</Box>
          </Stack>

          {/* Banner "in preparazione" */}
          {doc.in_preparazione && (
            <Box
              sx={{
                mt: 3,
                padding: '20px 24px',
                background: 'var(--bg-deep)',
                border: '1px solid var(--dust)',
                borderLeft: '3px solid var(--sun)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--sun-deep)',
                  mb: 0.6,
                }}
              >
                Documento in preparazione
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                  fontSize: '0.95rem',
                  color: 'var(--ink)',
                  lineHeight: 1.55,
                }}
              >
                I testi definitivi sono in fase di redazione legale e saranno pubblicati prima dell&apos;apertura ufficiale del marketplace al pubblico. La struttura del documento e le sezioni regolate sono gia quelle definitive.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Content area: TOC + body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
            gap: { xs: 3, md: 6 },
            alignItems: 'start',
          }}
        >
          {/* TOC */}
          <Box
            component="nav"
            aria-label="Indice del documento"
            sx={{
              position: { md: 'sticky' },
              top: { md: 32 },
              alignSelf: 'start',
            }}
          >
            {/* Mobile accordion */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <TocAccordion doc={doc} />
            </Box>
            {/* Desktop list */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TocList doc={doc} />
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ maxWidth: 720, lineHeight: 1.7, color: 'var(--ink)' }}>
            {doc.introduzione && (
              <Box sx={{ mb: 5 }}>
                {doc.introduzione.map((p, i) => (
                  <ParagraphRenderer key={i} paragrafo={p} />
                ))}
              </Box>
            )}
            {doc.sezioni.map((sez) => (
              <SectionBlock key={sez.id} sezione={sez} />
            ))}

            {doc.contatti_titolare && (
              <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid var(--dust)' }}>
                <Typography
                  sx={{
                    fontFamily: '"Fraunces Variable", Georgia, serif',
                    fontSize: '1.4rem',
                    fontWeight: 500,
                    color: 'var(--ink)',
                    mb: 2,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Contatti del Titolare
                </Typography>
                <Box sx={{
                  fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                  fontSize: '0.95rem',
                  color: 'var(--ink-soft)',
                  lineHeight: 1.7,
                }}>
                  <div><strong>{doc.contatti_titolare.ragione_sociale}</strong></div>
                  <div>{doc.contatti_titolare.indirizzo}</div>
                  <div>Email: {doc.contatti_titolare.email}</div>
                  {doc.contatti_titolare.pec && <div>PEC: {doc.contatti_titolare.pec}</div>}
                  {doc.contatti_titolare.pec_dpo && <div>PEC DPO: {doc.contatti_titolare.pec_dpo}</div>}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          padding: { xs: '24px', md: '24px 56px' },
          borderTop: '1px solid var(--dust)',
          mt: 6,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--ink-soft)',
          }}
        >
          Marketplace FV · Documenti legali · v{doc.versione}
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// Subcomponents
// ============================================================================

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function TocList({ doc }: { doc: LegalDocument }) {
  return (
    <Stack spacing={1.2}>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono Variable", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--ink-soft)',
          mb: 0.5,
        }}
      >
        Indice
      </Typography>
      {doc.sezioni.map((sez) => (
        <Box
          key={sez.id}
          component="a"
          href={`#${sez.id}`}
          sx={{
            display: 'flex',
            gap: 1,
            textDecoration: 'none',
            color: 'var(--ink-soft)',
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: '0.9rem',
            lineHeight: 1.4,
            py: 0.4,
            borderLeft: '2px solid transparent',
            pl: 1,
            transition: 'all 200ms',
            '&:hover': {
              color: 'var(--ink)',
              borderLeftColor: 'var(--sun)',
            },
          }}
        >
          <Box component="span" sx={{ color: 'var(--sun-deep)', minWidth: 18 }}>
            {sez.numero}.
          </Box>
          <span>{sez.titolo}</span>
        </Box>
      ))}
    </Stack>
  );
}

function TocAccordion({ doc }: { doc: LegalDocument }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      elevation={0}
      sx={{
        background: 'var(--bg-deep)',
        border: '1px solid var(--dust)',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.72rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--ink)',
          }}
        >
          Indice del documento
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TocList doc={doc} />
      </AccordionDetails>
    </Accordion>
  );
}

function SectionBlock({ sezione }: { sezione: LegalSection }) {
  return (
    <Box id={sezione.id} sx={{ mb: 5, scrollMarginTop: '20px' }}>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono Variable", monospace',
          fontSize: '0.7rem',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--sun-deep)',
          mb: 1,
        }}
      >
        Sezione {sezione.numero}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Fraunces Variable", Georgia, serif',
          fontSize: { xs: '1.6rem', md: '1.9rem' },
          fontWeight: 500,
          color: 'var(--ink)',
          mb: 2,
          letterSpacing: '-0.01em',
          lineHeight: 1.2,
        }}
      >
        {sezione.titolo}
      </Typography>
      {sezione.paragrafi.map((p, i) => (
        <ParagraphRenderer key={i} paragrafo={p} />
      ))}
      {sezione.sottosezioni && sezione.sottosezioni.length > 0 && (
        <Box sx={{ mt: 3, pl: { xs: 2, md: 3 }, borderLeft: '2px solid var(--dust)' }}>
          {sezione.sottosezioni.map((sub) => (
            <Box key={sub.id} id={sub.id} sx={{ mb: 3, scrollMarginTop: '20px' }}>
              <Typography
                component="h3"
                sx={{
                  fontFamily: '"Fraunces Variable", Georgia, serif',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  color: 'var(--ink)',
                  mb: 1.2,
                  letterSpacing: '-0.005em',
                }}
              >
                {sub.titolo}
              </Typography>
              {sub.paragrafi.map((p, i) => (
                <ParagraphRenderer key={i} paragrafo={p} />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function ParagraphRenderer({ paragrafo }: { paragrafo: LegalParagraph }) {
  const baseTextSx = {
    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.7,
    color: 'var(--ink)',
    mb: 1.8,
  };

  switch (paragrafo.type) {
    case 'text':
      return (
        <Typography component="p" sx={baseTextSx}>
          {renderInlineBold(paragrafo.text || '')}
        </Typography>
      );
    case 'bullets':
      return (
        <Box component="ul" sx={{ ...baseTextSx, pl: 3, mb: 2 }}>
          {(paragrafo.items || []).map((it, i) => (
            <Box component="li" key={i} sx={{ mb: 0.6 }}>{renderInlineBold(it)}</Box>
          ))}
        </Box>
      );
    case 'numbered':
      return (
        <Box component="ol" sx={{ ...baseTextSx, pl: 3, mb: 2 }}>
          {(paragrafo.items || []).map((it, i) => (
            <Box component="li" key={i} sx={{ mb: 0.6 }}>{renderInlineBold(it)}</Box>
          ))}
        </Box>
      );
    case 'quote':
      return (
        <Box
          component="blockquote"
          sx={{
            ...baseTextSx,
            pl: 2.5,
            py: 1,
            borderLeft: '3px solid var(--sun)',
            color: 'var(--ink-soft)',
            fontStyle: 'italic',
            background: 'var(--bg-deep)',
          }}
        >
          {renderInlineBold(paragrafo.text || '')}
        </Box>
      );
    case 'definition':
      return (
        <Box sx={{ mb: 1.5 }}>
          <Box component="span" sx={{
            fontFamily: '"Fraunces Variable", Georgia, serif',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--ink)',
            mr: 0.7,
          }}>
            {paragrafo.term}:
          </Box>
          <Box component="span" sx={{
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: '0.98rem',
            color: 'var(--ink)',
            lineHeight: 1.7,
          }}>
            {renderInlineBold(paragrafo.definition || '')}
          </Box>
        </Box>
      );
    case 'note':
      return (
        <Box sx={{
          padding: '14px 18px',
          background: 'var(--bg-deep)',
          border: '1px solid var(--dust)',
          borderLeft: '3px solid var(--sun)',
          mb: 2,
        }}>
          <Typography sx={{ ...baseTextSx, fontSize: '0.92rem', mb: 0 }}>
            {renderInlineBold(paragrafo.text || '')}
          </Typography>
        </Box>
      );
  }
}

/** Rendering minimo di **bold** in testo libero */
function renderInlineBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}
